import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';
import type { PdiComment } from './installations.api';

export type TicketStatus =
  | 'open' | 'assigned' | 'scheduled' | 'in_progress' | 'diagnosed'
  | 'pending_spare' | 'resolved' | 'closed' | 'reopened' | 'cancelled';

export interface ServiceSpare { id: number; itemId: number; warehouseId: number | null; quantity: number; status: string; inventoryTxnId: number | null; remarks: string | null }
export interface ServicePhoto { id: number; kind: string; fileName: string; fileUrl: string; capturedAt: string | null }
export interface ServiceHistory { id: number; fromStatus: string | null; toStatus: string; action: string; remarks: string | null; createdAt: string }

export interface ServiceTicket {
  id: number;
  ticketCode: string;
  installationId: number | null;
  dealerId: number | null;
  clientId: number | null;
  siteId: number | null;
  productId: number | null;
  regionId: number | null;
  ticketType: string;
  priority: string;
  source: string;
  status: TicketStatus;
  technicianId: number | null;
  vendorId: number | null;
  complaintDescription: string | null;
  scheduledDate: string | null;
  slaBreached: boolean;
  escalationLevel: number;
  warrantyValid: boolean;
  warrantyExpiry: string | null;
  amcValid: boolean;
  rootCause: string | null;
  resolutionRemarks: string | null;
  customerName: string | null;
  customerConfirmed: boolean;
  sparesApproved: boolean;
  remarks: string | null;
  createdAt: string;
  spares?: ServiceSpare[];
  photos?: ServicePhoto[];
  history?: ServiceHistory[];
}

export interface CreateTicketInput {
  installationId?: number | null;
  dealerId?: number | null;
  clientId?: number | null;
  siteId?: number | null;
  productId?: number | null;
  regionId?: number | null;
  ticketType?: string;
  priority?: string;
  source?: string;
  complaintDescription?: string;
}

export interface ServiceDashboard {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  open: number;
  pendingSpare: number;
  resolved: number;
  closed: number;
  slaBreached: number;
  recent: ServiceTicket[];
}

const base = createCrudApi<ServiceTicket, CreateTicketInput>('evcpd-service-tickets');
const post = async (id: number, path: string, body: Record<string, unknown> = {}): Promise<ServiceTicket> =>
  (await apiClient.post<ApiSuccess<ServiceTicket>>(`/evcpd-service-tickets/${id}/${path}`, body)).data.data;

export const serviceTicketsApi = {
  ...base,
  async dashboard(): Promise<ServiceDashboard> {
    return (await apiClient.get<ApiSuccess<ServiceDashboard>>('/evcpd-service-tickets/dashboard')).data.data;
  },
  assign: (id: number, body: { technicianId?: number | null; vendorId?: number | null }) => post(id, 'assign', body),
  schedule: (id: number, body: { scheduledDate: string; slaDueAt?: string }) => post(id, 'schedule', body),
  visit: (id: number) => post(id, 'visit'),
  diagnose: (id: number, body: { rootCause: string; remarks?: string }) => post(id, 'diagnose', body),
  addPhoto: (id: number, body: { kind: string; fileName: string; fileUrl: string }) => post(id, 'photos', body),
  addSpare: async (id: number, body: { itemId: number; quantity: number; warehouseId?: number | null }) =>
    (await apiClient.post<ApiSuccess<ServiceSpare>>(`/evcpd-service-tickets/${id}/spares`, body)).data.data,
  requestSpareApproval: (id: number) => post(id, 'spares/request-approval'),
  approveSpares: (id: number) => post(id, 'spares/approve'),
  rejectSpares: (id: number, reason: string) => post(id, 'spares/reject', { reason }),
  consumeSpare: (id: number, spareId: number, warehouseId: number) => post(id, `spares/${spareId}/consume`, { warehouseId }),
  resolve: (id: number, resolutionRemarks: string) => post(id, 'resolve', { resolutionRemarks }),
  confirm: (id: number, body: { customerName: string; signatureUrl?: string }) => post(id, 'confirm', body),
  close: (id: number) => post(id, 'close'),
  reopen: (id: number, reason: string) => post(id, 'reopen', { reason }),
  cancel: (id: number, reason: string) => post(id, 'cancel', { reason }),
  escalate: (id: number, reason: string) => post(id, 'escalate', { reason }),
  listComments: async (id: number): Promise<PdiComment[]> => (await apiClient.get<ApiSuccess<PdiComment[]>>(`/evcpd-service-tickets/${id}/comments`)).data.data,
  addComment: async (id: number, comment: string): Promise<PdiComment> => (await apiClient.post<ApiSuccess<PdiComment>>(`/evcpd-service-tickets/${id}/comments`, { comment })).data.data,
};
